import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Coins, Landmark, FileText, LandmarkIcon, ArrowRight } from 'lucide-react';

export default function Services() {
  const [activeServiceTab, setActiveServiceTab] = useState('accounts');

  // Handle Hash routing from Navbar dropdowns
  useEffect(() => {
    const handleHash = () => {
      const hash = window.location.hash;
      if (hash) {
        const tab = hash.replace('#', '');
        if (['accounts', 'transfers', 'deposits', 'loans'].includes(tab)) {
          setActiveServiceTab(tab);
        }
      }
    };
    handleHash();
    window.addEventListener('hashchange', handleHash);
    return () => window.removeEventListener('hashchange', handleHash);
  }, []);

  return (
    <div className="pt-[68px] bg-[#F7F9FC]">
      {/* Header Banner */}
      <section 
        className="relative overflow-hidden text-navy py-20 px-4 text-center bg-cover bg-center bg-no-repeat"
        style={{ backgroundImage: "url('/page header.png')" }}
      >
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_bottom,rgba(0,0,0,0.01),transparent)] pointer-events-none" />
        <div className="max-w-4xl mx-auto space-y-4 relative z-10">
          <span className="text-xs font-bold text-[#B31825] uppercase tracking-widest bg-[#B31825]/5 px-3 py-1 rounded-full w-fit mx-auto block">Banking Services</span>
          <h1 className="font-montserrat font-extrabold text-3xl sm:text-4xl text-navy">Smart Solutions for Daily & Future Needs</h1>
          <p className="text-slate-600 text-sm sm:text-base font-normal max-w-2xl mx-auto">
            We provide secure depository mechanisms, high-security lockup systems, and fast electronic settlement channels to make everyday transactions seamless.
          </p>
        </div>
      </section>

      {/* Services Nav Toggles (Reference Layout input_file_2.png style) */}
      <section className="py-8 bg-white border-b border-gray-200 sticky top-[72px] z-30 shadow-sm overflow-x-auto no-scrollbar">
        <div className="max-w-7xl mx-auto px-4 flex justify-start lg:justify-center space-x-2 sm:space-x-4">
          {[
            { id: 'accounts', name: 'Current Accounts & KYC' },
            { id: 'transfers', name: 'Current Funds Transfers' },
            { id: 'deposits', name: 'Deposit Schemes' },
            { id: 'loans', name: 'Loan Schemes' }
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => {
                setActiveServiceTab(tab.id);
                window.location.hash = `#${tab.id}`;
              }}
              className={`px-5 py-3 rounded-full font-montserrat font-bold text-xs sm:text-sm whitespace-nowrap transition-all duration-300 ${
                activeServiceTab === tab.id 
                  ? 'bg-[#B31825] text-white shadow-md' 
                  : 'bg-neutralBg text-navy hover:bg-gray-100 border border-gray-200'
              }`}
            >
              {tab.name}
            </button>
          ))}
        </div>
      </section>

      {/* Dynamic Content Sections */}
      
      {/* 1. ACCOUNTS & KYC SECTION */}
      {activeServiceTab === 'accounts' && (
        <section className="py-20 animate-fadeIn">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-16">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 items-center">
              {/* Left Box - Services Details */}
              <div className="lg:col-span-7 space-y-8">
                <div className="space-y-4">
                  <div className="p-3.5 bg-primary/10 text-primary rounded-2xl w-fit">
                    <Coins className="h-6 w-6" />
                  </div>
                  <h2 className="font-montserrat font-extrabold text-2xl sm:text-3xl text-navy">Sahyadri Savings & Current Accounts</h2>
                  <p className="text-gray-500 text-sm sm:text-base leading-relaxed font-light">
                    Open cooperative ledger accounts tailored to meet retail, agricultural, and commercial requirements.
                  </p>
                </div>
                
                <div className="space-y-5">
                  <div className="bg-white p-6 rounded-2xl border border-gray-200 shadow-sm hover:border-[#B31825] transition-colors relative overflow-hidden group">
                    <div className="absolute top-0 left-0 w-1.5 h-full bg-[#B31825]" />
                    <h4 className="font-montserrat font-extrabold text-base text-navy flex justify-between items-center">
                      <span>Savings Account</span>
                      <span className="bg-emerald/10 text-emerald text-[11px] font-bold px-2 py-0.5 rounded-full">4.00% p.a.</span>
                    </h4>
                    <p className="text-gray-500 text-xs sm:text-sm mt-2 leading-relaxed font-light">
                      Tailored for families and retail workers. Get direct access to ATM networks, free mobile passbook, check books, and instant UPI transactions. Minimum balance to open is just ₹500 (with checkbook ₹1,000).
                    </p>
                    <Link to="/savings-account" className="inline-flex items-center mt-3.5 text-xs font-bold text-[#B31825] hover:underline uppercase tracking-wider">
                      <span>View Account Details</span>
                      <ArrowRight className="h-3.5 w-3.5 ml-1.5" />
                    </Link>
                  </div>

                  <div className="bg-white p-6 rounded-2xl border border-gray-200 shadow-sm hover:border-primary transition-colors relative overflow-hidden group">
                    <div className="absolute top-0 left-0 w-1.5 h-full bg-[#052046]" />
                    <h4 className="font-montserrat font-extrabold text-base text-navy flex justify-between items-center">
                      <span>Current Account</span>
                      <span className="bg-primary/10 text-primary text-[11px] font-bold px-2 py-0.5 rounded-full font-mono">Business Tier</span>
                    </h4>
                    <p className="text-gray-500 text-xs sm:text-sm mt-2 leading-relaxed font-light">
                      Best for local traders, store owners, and small companies. Features unlimited withdrawals, zero transaction fee limits, NEFT/RTGS options, and bulk cash-collection support.
                    </p>
                    <Link to="/current-account" className="inline-flex items-center mt-3.5 text-xs font-bold text-[#052046] hover:underline uppercase tracking-wider">
                      <span>View Account Details</span>
                      <ArrowRight className="h-3.5 w-3.5 ml-1.5" />
                    </Link>
                  </div>
                
              </div>
              </div>

              {/* Right Box - Banner image frame */}
              <div className="lg:col-span-5">
                <div className="bg-white border border-gray-200 rounded-3xl p-3 shadow-xl overflow-hidden aspect-square flex items-center justify-center">
                  <img 
                    src="/services_accounts_banner.png" 
                    alt="Current Accounts & KYC Banner" 
                    className="w-full h-full object-cover rounded-2xl" 
                  />
                </div>
              </div>
            </div>

            {/* KYC document requirements */}
            <div className="bg-white border border-gray-200 rounded-3xl p-8 sm:p-12 shadow-md space-y-6 relative overflow-hidden">
              <div className="absolute top-0 right-0 w-24 h-24 bg-primary/5 rounded-bl-full" />
              <h3 className="font-montserrat font-extrabold text-lg text-navy flex items-center">
                <FileText className="h-5 w-5 mr-3 text-[#B31825]" />
                <span>Required KYC Documents</span>
              </h3>
              <p className="text-gray-400 text-xs font-light">Bring copy sets along with original credentials to the nearest branch desk.</p>
              
              <ul className="grid grid-cols-1 md:grid-cols-2 gap-4 text-xs sm:text-sm text-gray-500 font-light">
                <li className="flex items-start bg-neutralBg p-4 rounded-xl border border-gray-100">
                  <span className="text-[#B31825] mr-3 font-bold select-none">•</span>
                  <span><strong>PAN Card:</strong> Mandatory document for registration and taxation updates.</span>
                </li>
                <li className="flex items-start bg-neutralBg p-4 rounded-xl border border-gray-100">
                  <span className="text-[#B31825] mr-3 font-bold select-none">•</span>
                  <span><strong>Identity Proof:</strong> Aadhaar Card, Valid Passport, or Voter Registration Card.</span>
                </li>
                <li className="flex items-start bg-neutralBg p-4 rounded-xl border border-gray-100">
                  <span className="text-[#B31825] mr-3 font-bold select-none">•</span>
                  <span><strong>Address Proof:</strong> Recent Utility Bills (Electricity, Gas, or Landline Phone).</span>
                </li>
                <li className="flex items-start bg-neutralBg p-4 rounded-xl border border-gray-100">
                  <span className="text-[#B31825] mr-3 font-bold select-none">•</span>
                  <span><strong>Photographs:</strong> 2 Passport-sized color photos of the applicant.</span>
                </li>
                <li className="flex items-start bg-neutralBg p-4 rounded-xl border border-gray-100 md:col-span-2">
                  <span className="text-[#B31825] mr-3 font-bold select-none">•</span>
                  <span><strong>For Current Accounts:</strong> Shop Act License, GST registration, or registered Partnership Deeds.</span>
                </li>
              </ul>
            </div>
          </div>
        </section>
      )}

      {/* 2. FUNDS TRANSFERS SECTION */}
      {activeServiceTab === 'transfers' && (
        <section className="py-20 animate-fadeIn">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-16">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 items-center">
              {/* Left Box - Banner image frame */}
              <div className="lg:col-span-5">
                <div className="bg-white border border-gray-200 rounded-3xl p-3 shadow-xl overflow-hidden aspect-square flex items-center justify-center">
                  <img 
                    src="/services_transfers_banner.png" 
                    alt="Current Funds Transfers Banner" 
                    className="w-full h-full object-cover rounded-2xl" 
                  />
                </div>
              </div>

              {/* Right Box - Transfer info */}
              <div className="lg:col-span-7 space-y-6">
                <div className="p-3.5 bg-primary/10 text-primary rounded-2xl w-fit">
                  <Landmark className="h-6 w-6" />
                </div>
                <h2 className="font-montserrat font-extrabold text-2xl sm:text-3xl text-navy">Instant Transfers & NACH Facility</h2>
                <p className="text-gray-500 text-sm sm:text-base leading-relaxed font-light">
                  We offer RTGS, NEFT, and IMPS services under clearing-house guidelines. We also support the **NACH (National Automated Clearing House) Facility** to automate high-volume periodic payments like salary distributions, pensions, dividends, or recurring monthly loan EMI collection. Members can register mandate forms or process remittance requests directly at any Sahyadri branch.
                </p>
                <p className="text-gray-500 text-sm sm:text-base leading-relaxed font-light">
                  Transactions are updated in the customer ledger in real-time, sending immediate confirmation SMS alerts to the registered mobile numbers.
                </p>
              </div>
            </div>

            {/* Limits & Schedules Table */}
            <div className="bg-white border border-gray-200 rounded-3xl p-8 sm:p-12 shadow-md space-y-6 relative overflow-hidden">
              <h3 className="font-montserrat font-extrabold text-lg text-navy flex items-center">
                <LandmarkIcon className="h-5 w-5 mr-3 text-[#B31825]" />
                <span>Transfer Limits & Schedules</span>
              </h3>
              
              <div className="space-y-4 text-xs sm:text-sm font-sans">
                <div className="flex justify-between items-center border-b border-gray-150 pb-3">
                  <div className="space-y-0.5">
                    <span className="font-bold text-gray-700 block">RTGS (Instant Gross)</span>
                    <span className="text-[10px] text-gray-400 font-medium">Real-time settlements</span>
                  </div>
                  <span className="text-gray-800 font-bold text-right font-mono">Min: ₹2 Lakhs | Max: No Limit</span>
                </div>
                
                <div className="flex justify-between items-center border-b border-gray-150 pb-3">
                  <div className="space-y-0.5">
                    <span className="font-bold text-gray-700 block">NEFT (Batch Settlement)</span>
                    <span className="text-[10px] text-gray-400 font-medium">Processed in hourly batches</span>
                  </div>
                  <span className="text-gray-800 font-bold text-right font-mono">Min: ₹1 | Max: ₹10 Lakhs/day</span>
                </div>

                <div className="flex justify-between items-center border-b border-gray-150 pb-3">
                  <div className="space-y-0.5">
                    <span className="font-bold text-gray-700 block">IMPS (Immediate Transfer)</span>
                    <span className="text-[10px] text-gray-400 font-medium">24/7/365 immediate clearance</span>
                  </div>
                  <span className="text-gray-800 font-bold text-right font-mono">Min: ₹1 | Max: ₹2 Lakhs/Txn</span>
                </div>

                <div className="flex justify-between items-center border-b border-gray-150 pb-3">
                  <div className="space-y-0.5">
                    <span className="font-bold text-gray-700 block">NACH Facility (Bulk/EMI)</span>
                    <span className="text-[10px] text-gray-400 font-medium">Automated periodic processing</span>
                  </div>
                  <span className="text-gray-800 font-bold text-right font-light">Salaries, Pensions & EMI mandate</span>
                </div>

                <div className="flex justify-between items-center pt-2">
                  <span className="font-bold text-gray-700">Settlement Route Agent</span>
                  <span className="bg-[#052046] text-white text-[11px] font-bold px-3 py-1 rounded-full uppercase tracking-wider">ICICI Co-Op Channel</span>
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* 3. DEPOSIT SCHEMES SECTION (Landing Page related section) */}
      {activeServiceTab === 'deposits' && (
        <section className="py-20 animate-fadeIn">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-16">
            
            {/* Banner Segment */}
            <div className="bg-white border border-gray-250/70 rounded-3xl p-8 sm:p-12 shadow-sm grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
              <div className="lg:col-span-5 flex justify-center">
                <img 
                  src="/services_deposits_banner.png" 
                  alt="High-Yield Deposit Schemes" 
                  className="w-full max-w-[320px] rounded-2xl shadow-sm border border-gray-100" 
                />
              </div>
              <div className="lg:col-span-7 space-y-4">
                <span className="text-[#052046] font-montserrat font-bold text-xs uppercase tracking-widest bg-[#052046]/5 px-3 py-1 rounded-full w-fit block">Flexible Savings</span>
                <h3 className="font-montserrat font-extrabold text-2xl sm:text-3xl text-[#052046] tracking-tight leading-tight">
                  High-Yield Co-operative Deposit Schemes
                </h3>
                <p className="text-gray-500 text-sm leading-relaxed font-light">
                  Grow your wealth safely with our high-interest fixed deposits, flexible recurring deposit plans, and dedicated schemes for women and senior citizens.
                </p>
                <Link 
                  to="/deposits" 
                  className="inline-flex items-center space-x-2 px-6 py-3 bg-[#B31825] hover:bg-[#8a0f19] text-white font-bold rounded-xl text-xs uppercase tracking-wider transition-all shadow-sm"
                >
                  <span>Go to Deposit Calculator</span>
                  <ArrowRight className="h-3.5 w-3.5" />
                </Link>
              </div>
            </div>

            {/* Schemes Grid list */}
            <div className="space-y-6">
              <h4 className="font-montserrat font-extrabold text-xl text-navy">Our Specialized Deposit Products</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {[
                  { name: 'Savings Deposit', rate: '4.00% p.a.', desc: 'Perfect for regular savings with fully liquid withdrawal options.' },
                  { name: 'Fixed Deposit (FD)', rate: 'Up to 8.25% p.a.', desc: 'High yielding compound rates for lump-sum deposits with secure terms.' },
                  { name: 'Recurring Deposit (RD)', rate: 'Up to 8.00% p.a.', desc: 'Build up your capital with monthly deposits starting as low as ₹100.' },
                  { name: 'Senior Citizen Deposit', rate: 'Extra +0.50% p.a.', desc: 'Exclusive higher interest tiers to safeguard and grow retirement payouts.' },
                  { name: 'Women Savings Scheme', rate: 'Special Tiers', desc: 'Custom savings products for women to encourage financial independence.' }
                ].map((scheme, i) => (
                  <div key={i} className="bg-white border border-gray-200/80 rounded-3xl p-6 hover:border-[#B31825] hover:shadow-xl transition-all duration-300 flex flex-col justify-between group">
                    <div className="space-y-3">
                      <h5 className="font-montserrat font-extrabold text-base text-navy group-hover:text-[#B31825] transition-colors">{scheme.name}</h5>
                      <span className="inline-block text-xs font-bold text-emerald bg-emerald/10 px-3 py-1 rounded-full font-mono">{scheme.rate}</span>
                      <p className="text-gray-500 text-xs sm:text-sm font-light leading-relaxed">{scheme.desc}</p>
                    </div>
                    <div className="pt-4 mt-4 border-t border-gray-100 flex justify-end">
                      <Link to="/deposits" className="text-xs font-bold text-[#B31825] hover:underline flex items-center space-x-1 uppercase tracking-wider">
                        <span>Details & Calculator</span>
                        <ArrowRight className="h-3 w-3" />
                      </Link>
                    </div>
                  </div>
                ))}
              </div>
            </div>

          </div>
        </section>
      )}

      {/* 4. LOAN SCHEMES SECTION (Landing Page related section) */}
      {activeServiceTab === 'loans' && (
        <section className="py-20 animate-fadeIn">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-16">
            
            {/* Banner Segment */}
            <div className="bg-white border border-gray-250/70 rounded-3xl p-8 sm:p-12 shadow-sm grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
              <div className="lg:col-span-5 flex justify-center">
                <img 
                  src="/services_loans_banner.png" 
                  alt="Cooperative Loan Schemes" 
                  className="w-full max-w-[320px] rounded-2xl shadow-sm border border-gray-100" 
                />
              </div>
              <div className="lg:col-span-7 space-y-4">
                <span className="text-[#052046] font-montserrat font-bold text-xs uppercase tracking-widest bg-[#052046]/5 px-3 py-1 rounded-full w-fit block">Credit & Finance</span>
                <h3 className="font-montserrat font-extrabold text-2xl sm:text-3xl text-[#052046] tracking-tight leading-tight">
                  Flexible Low-Interest Cooperative Loans
                </h3>
                <p className="text-gray-500 text-sm leading-relaxed font-light">
                  From buying a home or a new vehicle to funding business expansions or personal needs, our affordable co-operative loan schemes feature minimal documentation and fast clearance.
                </p>
                <Link 
                  to="/loans" 
                  className="inline-flex items-center space-x-2 px-6 py-3 bg-[#B31825] hover:bg-[#8a0f19] text-white font-bold rounded-xl text-xs uppercase tracking-wider transition-all shadow-sm"
                >
                  <span>Explore Eligibility & Interest Rates</span>
                  <ArrowRight className="h-3.5 w-3.5" />
                </Link>
              </div>
            </div>

            {/* Schemes Grid list */}
            <div className="space-y-6">
              <h4 className="font-montserrat font-extrabold text-xl text-navy">Our Specialized Loan Products</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {[
                  { name: 'Personal Loan', rate: 'Starting at 11.5% p.a.', desc: 'Quick credit limits for emergencies, weddings, medical, or education costs.' },
                  { name: 'Home Loan', rate: 'Starting at 8.75% p.a.', desc: 'Make your dream home a reality with up to 30 years repayment tenure.' },
                  { name: 'Gold Loan', rate: 'Starting at 9.00% p.a.', desc: 'Instant cash limits against gold ornaments with minimal processing delays.' },
                  { name: 'Vehicle Loan', rate: 'Starting at 9.5% p.a.', desc: 'Attractive EMI financing options for new or pre-owned two/four wheelers.' },
                  { name: 'Business Loan', rate: 'Starting at 12.0% p.a.', desc: 'Working capital or term loans for micro, small, and medium local enterprises.' }
                ].map((scheme, i) => (
                  <div key={i} className="bg-white border border-gray-200/80 rounded-3xl p-6 hover:border-[#B31825] hover:shadow-xl transition-all duration-300 flex flex-col justify-between group">
                    <div className="space-y-3">
                      <h5 className="font-montserrat font-extrabold text-base text-navy group-hover:text-[#B31825] transition-colors">{scheme.name}</h5>
                      <span className="inline-block text-xs font-bold text-emerald bg-emerald/10 px-3 py-1 rounded-full font-mono">{scheme.rate}</span>
                      <p className="text-gray-500 text-xs sm:text-sm font-light leading-relaxed">{scheme.desc}</p>
                    </div>
                    <div className="pt-4 mt-4 border-t border-gray-100 flex justify-end">
                      <Link to="/loans" className="text-xs font-bold text-[#B31825] hover:underline flex items-center space-x-1 uppercase tracking-wider">
                        <span>Details & Documents</span>
                        <ArrowRight className="h-3 w-3" />
                      </Link>
                    </div>
                  </div>
                ))}
              </div>
            </div>

          </div>
        </section>
      )}

    </div>
  );
}
